import { useState } from "react";
import { Check, MoreHorizontal, Unlink, X } from "lucide-react";
import {
  awsResourceKey,
  type AwsResourceCandidate,
} from "@/lib/aws-resource-links";
import { useProjects, useProjectWriteAvailability } from "@/hooks/useProjects";
import {
  useAllProjectAwsResourceLinks,
  useCreateProjectAwsResourceLink,
  useDeleteProjectAwsResourceLink,
} from "@/hooks/useProjectAwsResourceLinks";
import { matchesSearchTokens } from "@/lib/search-utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function ProjectLinkMenu({
  resource,
  projectId,
}: {
  resource: AwsResourceCandidate;
  projectId?: string;
}) {
  const [search, setSearch] = useState("");
  const { data: projects } = useProjects();
  const { data: allLinks } = useAllProjectAwsResourceLinks();
  const createLink = useCreateProjectAwsResourceLink();
  const deleteLink = useDeleteProjectAwsResourceLink();
  const { canWrite, reason } = useProjectWriteAvailability();

  const resourceKey = awsResourceKey(resource);
  const resourceLinks = allLinks.filter(
    (link) => awsResourceKey(link) === resourceKey,
  );
  const linkedProjectIds = new Set(
    resourceLinks.map((link) => link.project_id),
  );
  const linkedCount = linkedProjectIds.size;
  const linkedProjects = projects.filter((project) =>
    linkedProjectIds.has(project.id),
  );
  const projectLabel = (project: (typeof projects)[number]) =>
    `${project.number ? `${project.number} - ` : ""}${project.name}`;

  const toggle = (projectId: string) => {
    const existing = resourceLinks.find(
      (link) => link.project_id === projectId,
    );
    if (existing) {
      deleteLink.mutate({ id: existing.id, projectId });
      return;
    }
    createLink.mutate({
      project_id: projectId,
      resource_type: resource.type,
      resource_id: resource.id ?? resource.arn ?? null,
      resource_arn: resource.arn ?? null,
      resource_name: resource.name ?? null,
      metadata: {},
    });
  };

  if (projectId) {
    const existing = resourceLinks.find(
      (link) => link.project_id === projectId,
    );
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label="Unlink from project"
        disabled={!canWrite || !existing}
        title={!canWrite ? (reason ?? "Read-only") : "Unlink from project"}
        onClick={() =>
          existing && deleteLink.mutate({ id: existing.id, projectId })
        }
      >
        <Unlink className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="relative inline-flex">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Link to projects"
            disabled={!canWrite}
            title={!canWrite ? (reason ?? "Read-only") : undefined}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
          {linkedCount > 0 && (
            <Badge
              variant="secondary"
              className="absolute -right-1 -top-1 h-4 min-w-4 justify-center rounded-full px-1 text-[10px] leading-none"
            >
              {linkedCount}
            </Badge>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="border-b px-3 py-2">
          <div className="text-xs font-medium text-muted-foreground">
            Linked projects
          </div>
          {linkedProjects.length === 0 ? (
            <div className="mt-1 text-xs text-muted-foreground">
              Not linked to any project yet.
            </div>
          ) : (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {linkedProjects.map((project) => (
                <Badge
                  key={project.id}
                  variant="secondary"
                  className="gap-1 pr-1 font-normal"
                >
                  <span className="max-w-40 truncate">
                    {projectLabel(project)}
                  </span>
                  <button
                    type="button"
                    aria-label={`Unlink ${projectLabel(project)}`}
                    disabled={!canWrite}
                    onClick={() => toggle(project.id)}
                    className="rounded-sm hover:bg-muted-foreground/20 disabled:opacity-50"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search projects…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No projects found.</CommandEmpty>
            <CommandGroup>
              {projects
                .filter((project) =>
                  matchesSearchTokens(projectLabel(project), search),
                )
                .map((project) => {
                  const linked = linkedProjectIds.has(project.id);
                  return (
                    <CommandItem
                      key={project.id}
                      value={project.id}
                      onSelect={() => toggle(project.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          linked ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {projectLabel(project)}
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
